// @flow
import React from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';
import { toDate } from 'date-functions';
import { colors } from 'bench-style-constants';

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MonthSelectorLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-column-gap: 1px;
  grid-row-gap: 1px;
  width: 100%;
  font-size: 14px;
`;

export const Month = styled.button`
  color: ${colors.charcoal};
  padding: 12px 8px;
  border: 0;
  border-radius: 3px;
  text-align: center;
  background: transparent;
  flex: 1;
  outline: none;
  ${props => props.highlight && `background: ${colors.sand}`};
  ${props => props.active && `background: ${colors.sand}`};
  transition: background-color 0.2s ease-out;

  &:focus {
    box-shadow: 0 0 5px 0 ${colors.water};
  }
`;

const isYearMonthInRange = (yearMonth, startYearMonth, endYearMonth) => {
  if (!startYearMonth) {
    return false;
  }

  if (!endYearMonth) {
    return yearMonth === startYearMonth;
  }

  const selectedDate = toDate(yearMonth);
  const startDate = toDate(startYearMonth);
  const endDate = toDate(endYearMonth);

  return selectedDate >= startDate && selectedDate <= endDate;
};

const isYearMonthHighlighted = (
  selectRange,
  yearMonth,
  hoveredYearMonth,
  selectedStartYearMonth,
  selectedEndYearMonth,
) => {
  const isSingleMonthSelected = selectedStartYearMonth === selectedEndYearMonth;

  if (selectRange && isSingleMonthSelected) {
    const isWithinRangeBeforeStart = isYearMonthInRange(
      yearMonth,
      hoveredYearMonth,
      selectedStartYearMonth,
    );
    const isWithinRangeAfterStart = isYearMonthInRange(
      yearMonth,
      selectedStartYearMonth,
      hoveredYearMonth,
    );

    return isWithinRangeBeforeStart || isWithinRangeAfterStart;
  }

  return yearMonth === hoveredYearMonth;
};

type MonthSelectorProps = {
  year: number,
  selectRange?: boolean,
  selectedStartYearMonth: string | null,
  selectedEndYearMonth: string | null,
  hoveredYearMonth: string | null,
  onDateSelect: (selectedYearMonth: string) => void,
  onMouseEnterMonth: (selectedYearMonth: string) => void,
  onMouseLeaveMonth: (selectedYearMonth: string) => void,
};

const MonthSelector = (props: MonthSelectorProps) => {
  const {
    year,
    selectRange,
    selectedStartYearMonth,
    selectedEndYearMonth,
    hoveredYearMonth,
    onDateSelect,
    onMouseEnterMonth,
    onMouseLeaveMonth,
  } = props;

  return (
    <MonthSelectorLayout>
      {months.map((month, index) => {
        const yearMonth = format(new Date(year, index), 'YYYY-MM');
        const active = isYearMonthInRange(yearMonth, selectedStartYearMonth, selectedEndYearMonth);

        const highlight = isYearMonthHighlighted(
          selectRange,
          yearMonth,
          hoveredYearMonth,
          selectedStartYearMonth,
          selectedEndYearMonth,
        );

        return (
          <Month
            active={active}
            highlight={!active && highlight}
            key={month}
            onClick={() => onDateSelect(yearMonth)}
            onMouseOver={() => onMouseEnterMonth(yearMonth)}
            onMouseOut={() => onMouseLeaveMonth(yearMonth)}
            onFocus={() => onMouseEnterMonth(yearMonth)}
            onBlur={() => onMouseLeaveMonth(yearMonth)}
            type="button"
            data-test-id={`month-${yearMonth}`}
          >
            {month}
          </Month>
        );
      })}
    </MonthSelectorLayout>
  );
};

MonthSelector.defaultProps = {
  selectRange: false,
  selectedStartYearMonth: null,
  selectedEndYearMonth: null,
};

export default MonthSelector;
